import { yupResolver } from '@hookform/resolvers/yup'
import { useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useSearchParams } from 'react-router-dom'
import * as yup from 'yup'

import AlertModal from '@/app/components/AlertModal/AlertModal'
import { ErrorMessage } from '@/app/components/ErrorMessage'
import { Input } from '@/app/components/Input'
import { OutlineButton } from '@/app/components/OutlineButton'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Headline4 } from '@/app/components/Typography'
import { useAuthLogin } from '@/app/hooks/auth/useAuthLogin'
import { GoogleLogo } from '@/app/icons/GoogleLogo'
import { MicrosoftLogo } from '@/app/icons/MicrosoftLogo'
import { MydraDuoLogo } from '@/app/icons/MydraDuoLogo/MydraDuoLogo'
import { isValidRedirect } from '@/app/lib/auth'
import { API_URL, ROUTES } from '@/app/lib/constants'
import { getErrorMessage } from '@/app/lib/getErrorMessage'
import * as CookieConsent from 'vanilla-cookieconsent'

type FormValues = {
  email: string
}

export function LoginPage() {
  const [searchParams] = useSearchParams()
  const intl = useIntl()

  const formSchema = yup.object<FormValues>().shape({
    email: yup
      .string()
      .email()
      .required(
        intl.formatMessage({
          id: 'Login.validationRequired',
          defaultMessage: 'This field is required',
        })
      ),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(formSchema),
  })

  const redirectTo = searchParams.get('redirectTo') ?? ROUTES.homepage
  const product = searchParams.get('product') ?? ''
  const campaign = searchParams.get('campaign') ?? ''

  const [serverResponse, setServerResponse] = useState<{
    message: string
    type: 'success' | 'error'
  }>({
    message: '',
    type: 'success',
  })
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false)
  const [isRedirectingToExternalLogin, setIsRedirectingToExternalLogin] =
    useState(false)
  const { mutateAsync } = useAuthLogin()

  const formRef = useRef<HTMLFormElement>(null)

  const handleOnSubmit = async (data: FormValues) => {
    try {
      await mutateAsync({
        ...data,
        ...(isValidRedirect(redirectTo) && {
          redirectTo: (() => {
            const encodedRedirectTo = encodeURIComponent(redirectTo)
            // absolute URL (external)
            if (redirectTo.startsWith('http')) {
              return encodedRedirectTo
            }
            // relative path
            return redirectTo.startsWith('/') ? redirectTo : `/${redirectTo}`
          })(),
        }),
      })
      setServerResponse({
        message: intl.formatMessage({
          id: 'Login.emailSent',
          defaultMessage: 'Check your email for a login link',
        }),
        type: 'success',
      })
    } catch {
      reset(
        {},
        {
          keepValues: true,
          keepErrors: true,
        }
      )
      setServerResponse({
        message: intl.formatMessage({
          id: 'Login.serverError',
          defaultMessage: 'Something went wrong. Please try again.',
        }),
        type: 'error',
      })
    } finally {
      if (!isAlertModalOpen) {
        setIsAlertModalOpen(true)
      }
    }
  }

  const getRedirectTo = (searchString: string) => {
    const parsed = new URLSearchParams(searchString)
    const redirectTo = parsed.get('redirectTo') || ''

    // we need to encode again because URLSearchParams automatically decodes all
    // params in the search string
    const encodedRedirectTo = encodeURIComponent(redirectTo)

    // if redirectTo is a full URL, just return it
    if (redirectTo.startsWith('http://') || redirectTo.startsWith('https://')) {
      return encodedRedirectTo
    }

    return (
      window.location.origin +
      (redirectTo.startsWith('/') ? encodedRedirectTo : '/' + encodedRedirectTo)
    )
  }

  const redirectToGoogleLogin = () => {
    setIsRedirectingToExternalLogin(true)

    if (typeof window === 'undefined') {
      return
    }

    const redirectUrl = `${API_URL}/auth/google?redirectUrl=${getRedirectTo(window.location.search)}`
    window.location.href = redirectUrl
  }

  const redirectToMicrosoftLogin = () => {
    setIsRedirectingToExternalLogin(true)

    if (typeof window === 'undefined') {
      return
    }

    const redirectUrl = `${API_URL}/auth/microsoft?redirectUrl=${getRedirectTo(window.location.search)}`
    window.location.href = redirectUrl
  }

  return (
    <>
      <Helmet>
        <style>
          {` .mydra-base-layout { min-height: 0; height: 100vh; } `}
        </style>
      </Helmet>
      <div className="relative flex flex-col h-full md:flex-row">
        <Link
          to={{ pathname: ROUTES.homepage, search: location.search }}
          className="z-10 text-black md:text-white md:absolute"
        >
          <MydraDuoLogo className="h-8 mx-auto mt-12 md:mt-7 md:ml-9" />
        </Link>
        <div className="relative flex-col items-center justify-center hidden gap-8 p-8 text-center md:w-1/2 md:flex">
          <div className="absolute w-full h-full p-2">
            <video
              className="object-cover w-full h-full rounded-xl filter brightness-[75%]"
              src="https://assets.mydra.io/videos/login-page-video.mp4"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            />
          </div>
          <Headline4 className="z-10 max-w-prose !text-4xl font-title text-white">
            {product === 'ai-business'
              ? intl.formatMessage({
                  id: 'Login.problem-into-ai-solution',
                  defaultMessage: 'Turn problem into AI solution',
                })
              : intl.formatMessage({
                  id: 'Login.make-your-own-path',
                  defaultMessage: 'Make your own path',
                })}
          </Headline4>
        </div>
        <div className="flex justify-center flex-1 w-full mt-6 overflow-y-auto md:mt-0 md:w-1/2 md:bg-cover">
          <div className="flex flex-col w-full bg-white md:my-auto md:w-80 md:mx-auto">
            <form
              className="container md:px-0 md:py-12"
              onSubmit={handleSubmit(handleOnSubmit)}
              ref={formRef}
            >
              <div className="flex flex-col">
                <Headline4 className="font-semibold text-center font-sans md:!text-3xl">
                  {intl.formatMessage({
                    id: 'Login.welcome',
                    defaultMessage: 'Welcome',
                  })}
                </Headline4>
                <p className="text-[--text] text-center text-xs md:text-sm">
                  {intl.formatMessage({
                    id: 'Login.login-into-mydra',
                    defaultMessage: 'Login to Mydra',
                  })}
                </p>
                <div className="flex flex-col gap-4 mt-6">
                  <Input
                    type="email"
                    id="email"
                    placeholder={intl.formatMessage({
                      id: 'Login.emailPlaceholder',
                      defaultMessage: 'Email',
                    })}
                    errorMessage={getErrorMessage('email', errors)}
                    {...register('email')}
                  />
                  <div className="flex flex-col gap-2 md:gap-4">
                    <PrimaryButton className="w-full" isLoading={isSubmitting}>
                      {intl.formatMessage({
                        id: 'Login.continue',
                        defaultMessage: 'Continue',
                      })}
                    </PrimaryButton>
                    <p className="mt-1 text-xs md:text-sm">
                      <FormattedMessage
                        id="Login.dont-have-account-signup"
                        defaultMessage="Don't have an account? {link}"
                        values={{
                          link: (
                            <Link
                              id="login-button--redirect-to-signup"
                              to={{
                                pathname: ROUTES.signup,
                                search: location.search,
                              }}
                              className="font-semibold text-center text-mydra-black"
                            >
                              {intl.formatMessage({
                                id: 'Login.signup',
                                defaultMessage: 'Sign up',
                              })}
                            </Link>
                          ),
                        }}
                      />
                    </p>
                  </div>
                  <hr
                    className="my-5 text-xs uppercase md:mt-2 border-lines hr-text"
                    data-content={intl.formatMessage({
                      id: 'Login.or',
                      defaultMessage: 'or',
                    })}
                  />
                  <OutlineButton
                    className="w-full !text-sm font-semibold"
                    onClick={(e) => {
                      e.preventDefault()
                      redirectToGoogleLogin()
                    }}
                    disabled={isRedirectingToExternalLogin}
                  >
                    <div className="flex items-center w-full">
                      <div className="flex-none w-6 ml-2 mr-4 flex justify-center">
                        <GoogleLogo />
                      </div>
                      <div className="flex-1 text-center">
                        {intl.formatMessage({
                          id: 'Login.continueWithGoogle',
                          defaultMessage: 'Continue with Google',
                        })}
                      </div>
                      <div className="flex-none w-6 mr-2"></div>{' '}
                      {/* Spacer for balance */}
                    </div>
                  </OutlineButton>
                  <OutlineButton
                    className="w-full !text-sm font-semibold"
                    onClick={(e) => {
                      e.preventDefault()
                      redirectToMicrosoftLogin()
                    }}
                    disabled={isRedirectingToExternalLogin}
                  >
                    <div className="flex items-center w-full">
                      <div className="flex-none w-6 ml-2 mr-4 flex justify-center">
                        <MicrosoftLogo />
                      </div>
                      <div className="flex-1 text-center">
                        {intl.formatMessage({
                          id: 'Login.continueWithMicrosoft',
                          defaultMessage: 'Continue with Microsoft',
                        })}
                      </div>
                      <div className="flex-none w-6 mr-2"></div>{' '}
                      {/* Spacer for balance */}
                    </div>
                  </OutlineButton>
                </div>
                <ErrorMessage
                  message={errors.root?.serverError?.message}
                  className="mt-2 text-left"
                />
                <button
                  type="button"
                  className="absolute bottom-0 right-0 p-4 text-sm "
                  onClick={() => {
                    CookieConsent.showPreferences()
                  }}
                >
                  Cookie preferences
                </button>
              </div>
            </form>
          </div>
        </div>
        <AlertModal
          message={serverResponse.message}
          isOpen={isAlertModalOpen}
          onClose={async (result) => {
            if (result) {
              formRef.current?.requestSubmit()
            }
            setIsAlertModalOpen(false)
          }}
        />
      </div>
    </>
  )
}
